// src/utils/validation.ts
import {  PaymentRequest, PaymentEnvironment } from '../types';

/**
 * Creates a WebPay configuration object with environment-based settings
 */

/**
 * Validates a payment request object
 */
export const validatePaymentRequest = (request: PaymentRequest): {
  isValid: boolean;
  errors: string[];
} => {
  const errors: string[] = [];

  // Required fields validation
  if (!request.txnRef || request.txnRef.trim().length === 0) {
    errors.push('Transaction reference (txnRef) is required');
  }

  if (!request.amount || request.amount <= 0) {
    errors.push('Amount must be greater than 0');
  }

  // Optional but recommended validations
  if (request.txnRef && request.txnRef.length > 50) {
    errors.push('Transaction reference should not exceed 50 characters');
  }

  if (request.amount && request.amount > 999999999) {
    errors.push('Amount exceeds maximum allowed value');
  }

  // Email validation if custId looks like an email
  if (request.custId && request.custId.includes('@')) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(request.custId)) {
      errors.push('Customer ID appears to be an email but is not valid');
    }
  }

  // Currency validation
  if (request.currency && !['566', '840', '978'].includes(String(request.currency))) {
    console.warn('Currency code may not be supported. Common codes: 566 (NGN), 840 (USD), 978 (EUR)');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Validates WebPay configuration
 */
export const validateWebPayConfig = (config: {
  merchantCode: string;
  payItemId: string;
  mode: string;
  scriptUrl?: string;
}): {
  isValid: boolean;
  errors: string[];
} => {
  const errors: string[] = [];

  if (!config.merchantCode || config.merchantCode.trim().length === 0) {
    errors.push('Merchant code is required');
  }

  if (!config.payItemId || config.payItemId.trim().length === 0) {
    errors.push('Pay item ID is required');
  }

  if (!['TEST', 'LIVE'].includes(config.mode)) {
    errors.push('Mode must be either TEST or LIVE');
  }

  if (config.scriptUrl && !isValidUrl(config.scriptUrl)) {
    errors.push('Script URL must be a valid URL');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Formats amount for display
 */
export const formatAmount = (
  amount: number, 
  currency: string = '566',
  locale: string = 'en-NG'
): string => {
  const currencyMap: Record<string, string> = {
    '566': 'NGN',
    '840': 'USD',
    '978': 'EUR'
  };

  const currencyCode = currencyMap[currency] || 'NGN';
  
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount / 100); // Assuming amount is in kobo/cents
  } catch (error) {
    // Fallback formatting
    const symbol = currencyCode === 'NGN' ? '₦' : '$';
    return `${symbol}${(amount / 100).toLocaleString()}`;
  }
};

/**
 * Generates a unique transaction reference
 */
export const generateTransactionRef = (prefix: string = 'txn'): string => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 8);
  return `${prefix}_${timestamp}_${random}`;
};

/**
 * Converts amount to kobo (for NGN) or cents (for other currencies)
 */
export const convertToMinorUnits = (amount: number, currency: string = '566'): number => {
  // Nigerian Naira uses kobo (1 NGN = 100 kobo)
  // Most other currencies use similar 100:1 ratio
    if (currency === '566') {
        return Math.round(amount * 100); // Convert NGN to kobo
    }
  return Math.round(amount * 100);
};

/**
 * Converts from kobo/cents back to major units
 */
export const convertFromMinorUnits = (amount: number): number => {
  return amount / 100;
};

// Helper function for URL validation
const isValidUrl = (string: string): boolean => {
  try {
    new URL(string);
    return true;
  } catch (_) {
    return false;
  }
};

// Export common validation regex patterns
export const validationPatterns = {
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  phone: /^\+?[\d\s\-\(\)]+$/,
  transactionRef: /^[a-zA-Z0-9_\-]{1,50}$/,
} as const;